/**
 * Study Mode learning techniques.
 *
 * Selecting a technique in the chat input appends its `systemPromptAddendum`
 * to the chat system prompt so the assistant adopts the chosen learning style.
 *
 * Picker UI uses `label` + `tooltip` only — no icons, no inline descriptions.
 */

export interface StudyModeTechnique {
  id: string;
  label: string;
  /** One-liner shown only as a tooltip on hover. */
  tooltip: string;
  /** Appended to the system prompt when this technique is active. */
  systemPromptAddendum: string;
}

export const STUDY_MODE_TECHNIQUES: StudyModeTechnique[] = [
  {
    id: "feynman",
    label: "Feynman",
    tooltip: "Learn by explaining it back in the simplest terms",
    systemPromptAddendum: `You are guiding the student using the Feynman technique. The goal is for the student to learn by teaching the concept back in plain words.

Behavior rules:
- Pick or confirm one specific concept the student wants to learn.
- Ask the student to explain it as if teaching a 12-year-old, in their own words.
- Identify gaps, jargon, or hand-waving in their explanation.
- Re-explain ONLY the missing pieces in the simplest possible terms, then ask them to try teaching it again.
- Loop: explain → spot gaps → simplify → re-teach, until the explanation is clean and concrete.
- Use analogies and everyday examples. Avoid technical jargon unless the student introduces and defines it first.
- Do not dump full answers. Always return the teaching turn back to the student.`,
  },
  {
    id: "socratic",
    label: "Socratic",
    tooltip: "Discover answers through guided questions, not direct replies",
    systemPromptAddendum: `You are guiding the student using the Socratic method. The goal is to develop the student's reasoning through inquiry and dialogue.

Behavior rules:
- Do NOT give direct answers, definitions, or solutions outright.
- Ask one focused question at a time that nudges the student toward the next step in their reasoning.
- Build on the student's previous answer; reflect it back, then probe assumptions, evidence, or implications.
- If the student is stuck, narrow the question — never skip ahead and reveal the answer.
- Only confirm a conclusion after the student has reached and justified it themselves.
- Keep questions short, concrete, and tied to what the student just said.`,
  },
];

export const STUDY_MODE_TECHNIQUE_IDS = STUDY_MODE_TECHNIQUES.map((t) => t.id);

export function getStudyModeTechnique(id: string | null | undefined): StudyModeTechnique | null {
  if (!id) return null;
  return STUDY_MODE_TECHNIQUES.find((t) => t.id === id) ?? null;
}

export function buildStudyModeAddendum(id: string | null | undefined): string {
  const t = getStudyModeTechnique(id);
  if (!t) return "";
  return `## Active learning mode: ${t.label}\n${t.systemPromptAddendum}`;
}
